import { Box, Typography } from '@mui/material';
import { useCallback, useMemo, type ReactElement } from 'react';
import { Navigate, Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { useHeaderActions, useTopHeader, useUserRoles } from 'app/hooks';
import ErrorState from 'app/shared-components/common/ErrorState';
import MarketingStationLayout from 'app/shared-components/MarketingStationLayout';
import { Tabs } from 'app/shared-components/structure';
import type { HealtheraTab } from 'app/shared-components/structure/Tabs';
import FuseSuspense from '@/@fuse/core/FuseSuspense';

import { UnpublishedChangesIndicator } from '../components/UnpublishedChangesIndicator';
import usePlusWebsites, { type PlusWebsite } from '../hooks/usePlusWebsites';
import { usePublishWebsite } from '../hooks/usePublishWebsite';
import { useWebBuilderAccess } from '../hooks/useWebBuilderAccess';
import { ONBOARDING_PATHS } from '../use-cases/Onboarding/constants/paths';

// A website can only be created after the user completes every onboarding step
// (colour, logo, homepage type, domain). Its existence is the completion signal.
function isOnboardingComplete(website: PlusWebsite | null): boolean {
  return website !== null;
}

const baseTabs: HealtheraTab[] = [
  { label: 'Content', value: 'content' },
  { label: 'Theme and Styles', value: 'theme-and-styles' },
];

const supportTabs: HealtheraTab[] = [
  ...baseTabs,
  { label: 'Admin settings', value: 'admin-settings' },
];

interface DomainLabelProps {
  value: string;
  isDeployed: boolean;
  muted?: boolean;
}

// A domain is only reachable once the website is deployed, so until then it is
// shown as plain text rather than a dead link.
function DomainLabel({ value, isDeployed, muted = false }: DomainLabelProps): ReactElement {
  const colour = muted ? 'vtwo:text-gray-500' : 'vtwo:text-typography-primary';

  if (!isDeployed) {
    return (
      <Typography variant="body2" className={colour}>
        {value}
      </Typography>
    );
  }

  return (
    <Typography
      component="a"
      href={`https://${value}`}
      target="_blank"
      rel="noopener noreferrer"
      variant="body2"
      className={`${muted ? 'vtwo:text-gray-500' : 'vtwo:text-blue-600'} vtwo:underline`}
    >
      {value}
    </Typography>
  );
}

interface WebsiteManagerViewProps {
  domain: string | null | undefined;
  alternativeDomain: string | null | undefined;
  showAlternativeDomain: boolean;
  isDeployed: boolean;
  hasUnpublishedChanges: boolean;
  tabs: HealtheraTab[];
  selectedTab: string;
  handleTabChange: (_e: unknown, val: string) => void;
  websiteId: string;
}

const WebsiteManagerView = ({
  domain,
  alternativeDomain,
  showAlternativeDomain,
  isDeployed,
  hasUnpublishedChanges,
  tabs,
  selectedTab,
  handleTabChange,
  websiteId,
}: WebsiteManagerViewProps): ReactElement => (
  <Box className="vtwo:grow vtwo:flex vtwo:flex-col vtwo:overflow-hidden">
    <Box className="vtwo:px-16 vtwo:py-12 vtwo:flex vtwo:justify-between vtwo:items-start vtwo:shrink-0">
      <Box className="vtwo:flex vtwo:flex-col vtwo:items-start">
        <Typography variant="h5" className="vtwo:font-bold">
          Manage your website
        </Typography>
        {domain && <DomainLabel value={domain} isDeployed={isDeployed} />}
        {showAlternativeDomain && alternativeDomain && (
          <DomainLabel value={alternativeDomain} isDeployed={isDeployed} muted />
        )}
      </Box>
      <Box className="vtwo:flex vtwo:items-center vtwo:gap-4">
        {hasUnpublishedChanges && <UnpublishedChangesIndicator />}
        <Box className="vtwo:flex vtwo:items-center vtwo:gap-4">
          <Typography variant="body2" className="vtwo:text-gray-500">
            Status:
          </Typography>
          <Typography
            variant="body2"
            className={`vtwo:font-600 ${isDeployed ? 'vtwo:text-teal-600' : 'vtwo:text-gray-400'}`}
          >
            {isDeployed ? 'Live' : 'Offline'}
          </Typography>
        </Box>
      </Box>
    </Box>

    <Box className="vtwo:px-16">
      <Tabs
        tabs={tabs}
        selectedTab={selectedTab}
        handleChange={handleTabChange}
        sx={{
          '& .MuiTab-root.Mui-selected': { color: 'black' },
          '& .MuiTabs-indicator': { backgroundColor: 'black' },
        }}
      />
    </Box>

    <Box className="vtwo:grow vtwo:overflow-y-auto">
      <FuseSuspense>
        <Outlet context={{ websiteId }} />
      </FuseSuspense>
    </Box>
  </Box>
);

const PlusWebsitesLayout = (): ReactElement => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const { plusWebsites, isLoading: websitesLoading } = usePlusWebsites();
  const {
    hasWebBuilderAccess,
    isHidden: builderHidden,
    isLoading: accessLoading,
    isError: accessError,
    retry: retryAccess,
  } = useWebBuilderAccess();
  const isLoading = websitesLoading || accessLoading;
  const { isSupport } = useUserRoles();

  const tabs = isSupport ? supportTabs : baseTabs;
  const lastSegment = pathname.split('/').filter(Boolean).pop() ?? '';
  const selectedTab = tabs.some((tab) => tab.value === lastSegment) ? lastSegment : 'content';

  useTopHeader('Website builder');

  const handleTabChange = useCallback(
    (_e: unknown, newTab: string) => {
      const newPathname = newTab === 'content' ? '/plus-websites' : `/plus-websites/${newTab}`;
      navigate({ pathname: newPathname, search: searchParams.toString() });
    },
    [navigate, searchParams]
  );

  const primaryWebsite = plusWebsites[0] ?? null;

  const { mutate: publishWebsite, isPending: isPublishing } = usePublishWebsite();

  const headerActions = useMemo(
    () =>
      primaryWebsite
        ? [
            {
              type: 'button' as const,
              title: isPublishing ? 'Publishing…' : 'Publish',
              callback: () => publishWebsite(primaryWebsite.id),
              disabled: !primaryWebsite.hasUnpublishedChanges || isPublishing,
            },
          ]
        : [],
    [primaryWebsite, isPublishing, publishWebsite]
  );

  useHeaderActions(headerActions);

  // Support users can always access admin-settings directly
  const isAdminSettingsPage = isSupport && lastSegment === 'admin-settings';

  if (!isAdminSettingsPage) {
    if (!isLoading && accessError) {
      return (
        <MarketingStationLayout>
          <ErrorState
            title="Something went wrong"
            subtitle="We couldn't verify your subscription. Please try again later."
            action={{ label: 'Try again', onClick: retryAccess }}
          />
        </MarketingStationLayout>
      );
    }

    if (!isLoading && builderHidden) return <Navigate to="/" replace />;

    if (!isLoading) {
      const onboardingDone = isOnboardingComplete(primaryWebsite);

      if (!onboardingDone || !hasWebBuilderAccess) {
        const startPath = hasWebBuilderAccess ? ONBOARDING_PATHS.colour : ONBOARDING_PATHS.base;
        return <Navigate to={{ pathname: startPath, search: searchParams.toString() }} replace />;
      }
    }
  }

  // No SnackbarProvider here. notistack binds the module-level enqueueSnackbar that queryClient
  // uses for global query/mutation error toasts in the *constructor* of the last provider
  // constructed, and never restores it on unmount — so nesting one steals every global toast and
  // drops it on the floor as soon as the user leaves this subtree. The app root provider serves
  // the whole tree.
  return (
    <MarketingStationLayout>
      <WebsiteManagerView
        domain={primaryWebsite?.domain}
        alternativeDomain={primaryWebsite?.alternativeDomain}
        showAlternativeDomain={isSupport}
        isDeployed={primaryWebsite?.isDeployed ?? false}
        hasUnpublishedChanges={primaryWebsite?.hasUnpublishedChanges ?? false}
        tabs={tabs}
        selectedTab={selectedTab}
        handleTabChange={handleTabChange}
        websiteId={primaryWebsite?.id ?? ''}
      />
    </MarketingStationLayout>
  );
};

export default PlusWebsitesLayout;
